<template>
  <div :class="`filter-date card form ${focused ? 'focused' : ''}`" @click="onFocus" v-click-outside="onBlur">
    <div class="form-group">
      <label>{{title}}</label>
      <div class="content">
        <InputSelect
          v-model="dateType"
          :searchable="true"
          :options="dateOption"
          :allowEmpty="false"
          :placeholder="$t('project.form.select_state')"
          :white-background="true"
          class="preview-select-status"        
        />
        <div v-if="showDateTimePicker">
          <date-range-picker
            name="from"
            :value="range"
            inputType="date"
            valueType="YYYY-MM-DD HH:mm:00"
            :range="true"
            format="MMM DD YYYY HH:mm"
            :clearable="false"
            range-separator=" - "
            @onInputChange="onDateInputChanged"
            @closing="onClose"
            calendarButtonIcon="fas fa-calendar-alt"
            :disabled="disabled"
          >
            <template slot="footer">
              <div class="d-flex time-pickers">
                <div class="time">
                  <time-picker :hours="fromHour" :minutes="fromMinutes" @update="(value) => onChangeTime('from', value)" />
                </div>
                <div class="time">
                  <time-picker :hours="toHour" :minutes="toMinutes" @update="(value) => onChangeTime('to', value)" />
                </div>
              </div>
            </template>
          </date-range-picker>
        </div>
        <template v-else>
          <div class="from">
            <datepicker
              name="from"
              v-model="dateFrom"
              format="dd MMM yyyy"
              placeholder="From"
              :maximum="dateTo"
              :clearButton="true"
              :disabled="disabled"
            />
          </div>
          <span class="separator">
            <i class="fas fa-long-arrow-right"></i>
          </span>
          <div class="to">
            <datepicker
              name="to"
              v-model="dateTo"
              :useUtc="false"
              format="dd MMM yyyy"
              placeholder="To"
              :minimum="dateFrom"
              :clearButton="true"
              :disabled="disabled"
            />
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import Datepicker from "../datepicker";
import DateRangePicker from "../date-picker-2";
import { ARTICLES } from "../../config/const";
import InputSelect from '../../components/form-components/input-select';
import TimePicker from '../form-components/time-picker';

export default {
  props: {
    onChange: Function,
    disabled: Boolean,
    title: String,
    type: {
      type: String,
      required: false,
      default: ARTICLES.FILTERS.PUBLISHED_AT,
    },
    from: {
      type: String,
      required: false,
      default: () => {
        return moment.utc().format();
      },
    },
    to: {
      type: String,
      required: false,
      default: () => {
        return moment.utc().format();
      },
    },
    showDateTimePicker: {
      type: Boolean,
      default: () => false
    },
    formatDate: {
      type: Function,
      default: (value) => value && moment.utc(value).format('YYYY-MM-DD')
    }
  },

  data: function() {

    const fromDate = moment.utc(this.from);
    const toDate = moment.utc(this.to);

    return {
      focused: false,
      dateOption: [
        {
          label: this.$t('articles.published_at'),
          value: ARTICLES.FILTERS.PUBLISHED_AT,
        },
      ],
      dateOptionBk: [
        {
         label: this.$t('articles.created_at'),
         value: ARTICLES.FILTERS.CREATED_AT,
        },
        {
          label: this.$t('articles.published_at'),
          value: ARTICLES.FILTERS.PUBLISHED_AT,
        },
      ],

      fromDate: fromDate.toDate(),
      fromHour: fromDate.format("HH"),
      fromMinutes: fromDate.format("mm"),
      toDate: toDate.toDate(),
      toHour: toDate.format("HH"),
      toMinutes: toDate.format("mm")
    }
  },

  computed: {
    dateFrom: {
      get() {
        return this.from && moment.utc(this.from).toDate();
      },
      set(newValue) {
        this.$emit('update:from', this.formatDate(newValue));
      }
    },
    dateTo: {
      get() {
        return this.to && moment.utc(this.to).toDate();
      },
      set(newValue) {
        this.$emit('update:to', this.formatDate(newValue))
      }
    },
    range () {
      return [moment.utc(this.fromDate).format('YYYY-MM-DD HH:mm:00'), moment.utc(this.toDate).format('YYYY-MM-DD HH:mm:00')];
    },
    dateType: {
      get() {
        return this.type;
      },
      set(newValue) {
        this.$emit('update:type', newValue);
      }
    }
  },

  methods: {
    onFocus: function() {
      this.focused = true;
    },

    onBlur: function() {
      this.focused = false;
    },

    onDateInputChanged: function(value) {
      const from = moment.utc(value[0]).set({ hour: this.fromHour, minute: this.fromMinutes }).toDate();
      const to = moment.utc(value[1]).set({ hour: this.toHour, minute: this.toMinutes }).toDate();
      this.fromDate = from;
      this.toDate = to;
    },

    onClose: function() {
      if (this.fromDate.getTime() !== moment.utc(this.from).toDate().getTime() || this.toDate.getTime() !== moment.utc(this.to).toDate().getTime()) {
        this.$emit('updateRange', [moment.utc(this.fromDate).toDate(), moment.utc(this.toDate).toDate()]);
      }
    },

    onChangeTime(key, value) {
      if (key === 'from') {
        this.fromHour = value.hours;
        this.fromMinutes = value.minutes;
      } else if (key === 'to') {
        this.toHour = value.hours;
        this.toMinutes = value.minutes;
      }
    }
  },

  watch: {
    fromHour (value) {
      this.fromDate = moment.utc(this.fromDate).set({ hour: value }).toDate();
    },
    fromMinutes (value) {
      this.fromDate = moment.utc(this.fromDate).set({ minute: value }).toDate();
    },
    toHour (value) {
      this.toDate = moment.utc(this.toDate).set({ hour: value }).toDate();
    },
    toMinutes (value) {
      this.toDate = moment.utc(this.toDate).set({ minute: value }).toDate();
    }
  },

  components: {
    InputSelect,
    Datepicker,
    DateRangePicker,
    TimePicker
  }
};
</script>

<style lang="scss" scoped>
  .filter-date {
    position: relative;
    flex: 0 1 0%;
    font-size: 12px;

    &.focused {
      z-index: 3;
    }

    .content {
      display: flex;
      align-items: center;
    }

    .separator {
      margin: 0 15px;
      color: #CCC;
    }

    .to,
    .from {
      :deep(.dp-wrapper) {
        width: 100px;

        & > div:not(.dp-calendar) {
          display: flex;
          align-items: center;
        }

        .dp-input {
          width: 85px;

          &[disabled] + .vdp-datepicker__clear-button {
            pointer-events: none;
          }
        }

        .vdp-datepicker__clear-button {
          font-size: 14px;
          padding: 0 4px;
        }
      }
    }
    
    label {
      // width: 100px;
      padding: 0px 8px;
    }
  }

  .mx-datepicker-range {
    width: 290px;

    &:deep(.mx-input) {
      border: 0;
      outline: 0;
      box-shadow: none;
      font-size: inherit;
      color: inherit;

      &:hover {
        background-color: #f1f1f1;
      }
    }
  }

  .time-pickers {
    justify-content: space-between;

    .time {
      flex: 1 1 50%;
      display: flex;
    }
  }
</style>